import styled from 'styled-components';

import DonutChart from 'components/shared/charts/DonutChart';
import CircularProgress from 'components/shared/CircularProgress';
import { BodySmall } from 'styles/typography';

import { REFERRAL_STATUS_LABELS, REFERRAL_STATUSES, STATUS_COLORS } from '../constants';

type StatusCounts = Record<string, number>;

type Props = {
  statusCounts: StatusCounts;
  totalReferrals: number;
  loading: boolean;
  onSliceClick?: (status: string) => void;
};

export default function ReferralStatusDonutChart({ statusCounts, totalReferrals, loading, onSliceClick }: Props) {
  if (loading) {
    return (
      <ChartContainer>
        <StyledProgress />
      </ChartContainer>
    );
  }

  const clickableStatuses = new Set(['accepted', 'rejected']);
  const chartData = REFERRAL_STATUSES.map((status) => {
    const clickable = !!onSliceClick && clickableStatuses.has(status);
    return {
      name: REFERRAL_STATUS_LABELS[status],
      y: statusCounts[status] ?? 0,
      color: STATUS_COLORS[status],
      ...(clickable && {
        cursor: 'pointer' as const,
        events: { click: () => onSliceClick(status) },
      }),
    };
  });

  return (
    <ChartContainer>
      <ChartLegendRow>
        <DonutChart
          data={chartData}
          centerValue={totalReferrals}
          centerLabel='Total'
          config={{
            chart: {
              height: 200,
              width: 200,
            },
            title: { y: 14 },
            legend: { enabled: false },
            plotOptions: {
              pie: {
                size: 200,
                innerSize: '50%',
                center: ['50%', '50%'],
              },
            },
          }}
        />
        <Legend>
          {chartData.map((item) => (
            <LegendItem key={item.name}>
              <LegendSwatch $color={item.color} />
              <LegendLabel>
                {item.name}: {item.y}
              </LegendLabel>
            </LegendItem>
          ))}
        </Legend>
      </ChartLegendRow>
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;

  @media (max-width: 800px) {
    padding: 24px 16px;
  }
`;

const ChartLegendRow = styled.div`
  display: flex;
  align-items: center;

  > :first-child {
    flex: 0 0 200px;
    padding-right: clamp(0px, 3vw, 20px);
  }

  @media (max-width: 800px) {
    flex-direction: column;
    gap: 16px;
    align-items: center;

    /* Highcharts injects inline flex:1 1 0 and overflow:hidden on its wrapper, which collapse
       to 0 height in a column-direction parent and clip the chart — override both. */
    > :first-child {
      flex: none !important;
      width: 200px;
      height: 200px;
      overflow: visible !important;
      padding-right: 0;
    }
  }
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: clamp(0px, 3vw, 20px);

  @media (max-width: 800px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 24px;
    padding-left: 0;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LegendSwatch = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendLabel = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
`;

const StyledProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.colors.primaryBlue};
`;
